import {
  content_v1_DatasourcePreset,
  core_v1_DataSource,
} from '@antimatterhq/asl-api-ts-v1';
import { produce, WritableDraft } from 'immer';
import { deepEqual } from './datasourceUtils';
import { clearAutoloader, getAutoloader, setAutoloader } from './uiStatus';
import { BronzeDataSourceSchemaValues } from '@/zod/schemas/dataSource/BronzeDataSource';

export const getBronzeInitialValues = (
  dataSource: core_v1_DataSource,
): BronzeDataSourceSchemaValues => {
  const name = dataSource.spec.bronze?.bronzeTable || '';
  const skip = dataSource.spec.bronze?.skipBronzeLoading || false;
  const format = dataSource.spec.autoloader?.format || '';
  const schemaFile = dataSource.spec.autoloader?.schemaFile || '';
  const schemaHintsFile =
    dataSource.spec.autoloader?.cloudFiles?.schemaHintsFile || '';
  const schemaHints = dataSource.spec.autoloader?.cloudFiles?.schemaHints || '';
  const location = dataSource.spec.autoloader?.location || '';

  const schema = schemaHintsFile
    ? 'hintsFile'
    : schemaHints
      ? 'hints'
      : schemaFile
        ? 'file'
        : 'hints';

  return {
    name,
    format,
    schema,
    schemaFile,
    schemaHintsFile,
    schemaHints,
    skip,
    location,
  };
};

export const getIsBronzeCompleted = (dataSource: core_v1_DataSource) => {
  const name = dataSource.spec.bronze?.bronzeTable ?? '';
  const skip = dataSource.spec.bronze?.skipBronzeLoading ?? false;
  const format = dataSource.spec.autoloader?.format ?? '';
  const location = dataSource.spec.autoloader?.location ?? '';

  if (!name) {
    return false;
  }

  if (skip) {
    return true;
  }

  if (!format) {
    return false;
  }

  if (!location) {
    return false;
  }

  return true;
};

export const setBronzePresets = (
  dataSource: core_v1_DataSource,
  preset: content_v1_DatasourcePreset | undefined,
) => {
  return produce(
    dataSource,
    (draftState: WritableDraft<core_v1_DataSource>) => {
      draftState.spec.bronze = {
        ...draftState.spec.bronze,
        bronzeTable:
          preset?.name || draftState.spec.bronze?.bronzeTable || undefined,
      };
      draftState.spec.autoloader = {
        location: draftState.spec.autoloader?.location || '',
        format:
          preset?.autoloader?.format ||
          draftState.spec.autoloader?.format ||
          undefined,
        schemaFile:
          preset?.autoloader?.schemaFile ||
          draftState.spec.autoloader?.schemaFile ||
          undefined,
        cloudFiles: {
          schemaHintsFile:
            preset?.autoloader?.cloudFiles?.schemaHintsFile ||
            draftState.spec.autoloader?.cloudFiles?.schemaHintsFile ||
            undefined,
          schemaHints:
            preset?.autoloader?.cloudFiles?.schemaHints ||
            draftState.spec.autoloader?.cloudFiles?.schemaHints ||
            undefined,
        },
      };
    },
  );
};

export const setBronzeFormValues = (
  dataSource: core_v1_DataSource,
  values: BronzeDataSourceSchemaValues,
) => {
  const {
    name,
    format,
    schema,
    schemaFile,
    schemaHints,
    schemaHintsFile,
    location,
    skip: skipBronzeLoading,
  } = values;

  return produce(
    dataSource,
    (draftState: WritableDraft<core_v1_DataSource>) => {
      draftState.spec.bronze = {
        bronzeTable: name,
      };

      draftState.spec.autoloader = undefined;
      draftState.spec.bronze.skipBronzeLoading = skipBronzeLoading;

      // Silver bronze tables should always include the bronzeTable
      if (!draftState.spec.silver) {
        draftState.spec.silver = {};
      }

      if (!draftState.spec.silver?.bronzeTables) {
        draftState.spec.silver.bronzeTables = [];
      }

      if (
        !draftState.spec.silver.bronzeTables.map((q) => q.name).includes(name)
      ) {
        draftState.spec.silver.bronzeTables = [{ name }];
      }

      if (skipBronzeLoading) {
        return;
      }

      draftState.spec.autoloader = {
        location: location ?? '',
        format,
      };

      switch (schema) {
        case 'file':
          draftState.spec.autoloader.schemaFile = schemaFile || undefined;
          break;
        case 'hints':
          draftState.spec.autoloader.cloudFiles = schemaHints
            ? { schemaHints }
            : undefined;
          break;
        case 'hintsFile':
          draftState.spec.autoloader.cloudFiles = schemaHintsFile
            ? { schemaHintsFile }
            : undefined;
          break;
      }
    },
  );
};

export const bronzeHasUnsavedChanges = (
  datasource: core_v1_DataSource,
  unsavedDatasource: core_v1_DataSource | undefined,
) => {
  if (!unsavedDatasource) {
    return false;
  }
  return (
    !deepEqual(datasource.spec.autoloader, unsavedDatasource.spec.autoloader) ||
    !deepEqual(datasource.spec.bronze, unsavedDatasource.spec.bronze)
  );
};

export const autoloaderCheck = (datasource: core_v1_DataSource) => {
  const autoloader = datasource.spec.autoloader;
  if (!autoloader) {
    return datasource;
  }
  if (!autoloader.location && autoloader.format) {
    const newDatasource = setAutoloader(datasource, autoloader);
    return produce(
      newDatasource,
      (draftState: WritableDraft<core_v1_DataSource>) => {
        draftState.spec.autoloader = undefined;
      },
    );
  }

  if (autoloader.location) {
    return clearAutoloader(datasource);
  }

  return datasource;
};

export const autoloaderApply = (datasource: core_v1_DataSource) => {
  const autoloader = getAutoloader(datasource);
  if (!autoloader) {
    return datasource;
  }

  return produce(
    datasource,
    (draftState: WritableDraft<core_v1_DataSource>) => {
      draftState.spec.autoloader = autoloader;
    },
  );
};
